import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  timeoutMs: number
  labelKey: string
}
const clampProgress = (progress: number) => Math.min(100, Math.max(0, progress))

const ProgressBar = ({ timeoutMs, labelKey }: Props) => {
  const { t } = useTranslation()
  const timeoutSeconds = Math.ceil(timeoutMs / 1000)
  const [progress, setProgress] = useState({
    amount: 0,
    secondsRemaining: timeoutSeconds,
  })

  useEffect(() => {
    const scanStartedAt = Date.now()

    const scanProgressInterval = window.setInterval(() => {
      const elapsedMs = Date.now() - scanStartedAt
      const nextProgress = Math.min(100, (elapsedMs / timeoutMs) * 100)
      const nextSecondsRemaining = Math.max(0, Math.ceil((timeoutMs - elapsedMs) / 1000))

      setProgress({
        amount: nextProgress,
        secondsRemaining: nextSecondsRemaining,
      })
    }, 250)

    return () => {
      window.clearInterval(scanProgressInterval)
    }
  }, [timeoutMs])

  return (
    <Box
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={timeoutSeconds}
      aria-valuenow={timeoutSeconds - progress.secondsRemaining}
      aria-label={t('progressBar')}
      width="100%"
      maxWidth="400px"
    >
      <Box width="100%" height="12px" overflow="hidden" borderRadius="999px" bgcolor="grey.300">
        <Box
          width={`${clampProgress(progress.amount)}%`}
          height="100%"
          borderRadius="999px"
          bgcolor="info.main"
          sx={{ transition: 'width 250ms linear' }}
        />
      </Box>
      <Typography variant="body2" color="text.primary" marginTop="8px" textAlign="center">
        {t(labelKey, {
          seconds: progress.secondsRemaining,
        })}
      </Typography>
    </Box>
  )
}

ProgressBar.displayName = 'ProgressBar'

export default ProgressBar
